import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "./Footer";
import Navbar from "./Navbar";

const cookieInventory = [
  { name:"fb_session",    category:"Strictly Necessary",    purpose:"Maintains your authenticated session while logged in",                 duration:"Session",  thirdParty:"No" },
  { name:"fb_csrf",       category:"Strictly Necessary",    purpose:"Prevents cross-site request forgery attacks",                           duration:"Session",  thirdParty:"No" },
  { name:"fb_age_verify", category:"Strictly Necessary",    purpose:"Records age confirmation at registration (18+ gate)",                   duration:"1 year",   thirdParty:"No" },
  { name:"fb_prefs",      category:"Functional",            purpose:"Stores your language and display preferences",                          duration:"1 year",   thirdParty:"No" },
  { name:"fb_pet_id",     category:"Functional",            purpose:"Remembers your active pet profile selection",                           duration:"30 days",  thirdParty:"No" },
  { name:"fb_consent",    category:"Strictly Necessary",    purpose:"Records your cookie consent preferences",                               duration:"1 year",   thirdParty:"No" },
  { name:"_stripe_sid",   category:"Strictly Necessary",    purpose:"Stripe session management for payment processing",                      duration:"30 min",   thirdParty:"Yes (Stripe)" },
  { name:"_stripe_mid",   category:"Strictly Necessary",    purpose:"Stripe machine identifier for fraud prevention",                        duration:"1 year",   thirdParty:"Yes (Stripe)" },
  { name:"_ga",           category:"Performance/Analytics", purpose:"Google Analytics: distinguishes unique users",                          duration:"2 years",  thirdParty:"Yes (Google)" },
  { name:"_ga_[ID]",      category:"Performance/Analytics", purpose:"Google Analytics: maintains session state",                             duration:"2 years",  thirdParty:"Yes (Google)" },
  { name:"_gid",          category:"Performance/Analytics", purpose:"Google Analytics: distinguishes users, 24-hour window",                 duration:"24 hours", thirdParty:"Yes (Google)" },
  { name:"_gat",          category:"Performance/Analytics", purpose:"Google Analytics: throttles request rate",                              duration:"1 minute", thirdParty:"Yes (Google)" },
];

const categoryColor = {
  "Strictly Necessary":    "bg-[#967d6c] text-white",
  "Functional":            "bg-[#bda697] text-white",
  "Performance/Analytics": "bg-gray-200 text-gray-700",
};

const sections = [
  {
    number: "1",
    title: "What Are Cookies?",
    content: `Cookies are small text files placed on your device when you access a website or online service. They are widely used to make websites work, improve efficiency, and provide information to website owners.

In addition to cookies, we may use other technologies such as web beacons ("pixel tags" or "clear GIFs"), local storage objects (LSOs), session replay technologies, and similar mechanisms. This Policy refers to all such technologies collectively as "cookies."

BY USING THE SERVICE, YOU CONSENT TO OUR USE OF COOKIES AS DESCRIBED IN THIS POLICY AND OUR PRIVACY POLICY. If you do not consent, you must adjust your browser settings or cease using the Service.`,
  },
  {
    number: "2",
    title: "Categories of Cookies We Use",
    subsections: [
      {
        title: "2.1 Strictly Necessary Cookies",
        content: `These cookies are essential to enable you to use the features of the Service and cannot be switched off. They are set in response to your actions such as setting privacy preferences, logging in, or filling in forms, as well as verifying your age at registration.

Strictly necessary cookies include our age verification cookie (fb_age_verify) which records confirmation that you are 18 or older. This cookie supports our compliance with our 18+ age requirement and COPPA obligations.

You cannot opt out of strictly necessary cookies without impairing the functionality of the Service.`,
      },
      {
        title: "2.2 Performance and Analytics Cookies",
        content: `These cookies allow us to count visits and traffic sources to measure and improve Service performance. They help us understand which pages are most and least popular and how users navigate the site. All information collected is aggregated and anonymous. These cookies require your consent in jurisdictions where consent is mandated.`,
      },
      {
        title: "2.3 Functional Cookies",
        content: `These cookies enable enhanced functionality and personalization, including remembering your language preferences, active pet profile, and other settings. If you disable these cookies, some personalized features may not function properly.`,
      },
      {
        title: "2.4 Targeting and Advertising Cookies",
        content: `Furbabies.AI does not currently serve advertising to users and does not use targeting or advertising cookies. In the event we implement advertising features in the future, we will update this Policy and obtain required consents prior to deploying such cookies.`,
      },
      {
        title: "2.5 Third-Party Cookies",
        content: `Some cookies are set by third parties including Stripe (payment processing) and Google (analytics). These third parties may collect information about your online activities across different websites. We do not control third-party cookies. Their use is governed by the respective third party's privacy and cookie policies.

Third-party cookie providers we use include:
• Stripe, Inc. — stripe.com/privacy
• Google LLC — policies.google.com/privacy`,
      },
    ],
  },
  {
    number: "4",
    title: "Your Cookie Choices and Opt-Outs",
    subsections: [
      {
        title: "4.1 Cookie Consent Banner",
        content: `When you first access the Service, you will be presented with a cookie consent banner allowing you to accept, reject, or customize non-essential cookies. You can update your preferences at any time through the cookie settings tool in our website footer.

Your consent choices are recorded in the fb_consent cookie. Withdrawing consent for non-essential cookies will not affect your ability to use core Service features.`,
      },
      {
        title: "4.2 Browser Controls",
        content: `Most browsers allow you to control cookies through their settings. You can block all cookies, block third-party cookies only, clear cookies when you close the browser, or receive alerts when a cookie is set.

• Google Chrome: Settings > Privacy and Security > Cookies and other site data
• Mozilla Firefox: Options > Privacy & Security > Cookies and Site Data
• Apple Safari: Preferences > Privacy > Manage Website Data
• Microsoft Edge: Settings > Cookies and site permissions
• Opera: Settings > Advanced > Privacy & security > Site Settings > Cookies

Note: Blocking strictly necessary cookies (including the age verification cookie) will prevent you from using the Service.`,
      },
      {
        title: "4.3 Analytics Opt-Out",
        content: `To opt out of Google Analytics: install the Google Analytics Opt-out Browser Add-on at tools.google.com/dlpage/gaoptout. For other analytics providers, consult their respective opt-out mechanisms.`,
      },
      {
        title: "4.4 Mobile Devices",
        content: `On mobile devices, you may control certain tracking through device settings:
• iOS: Settings > Privacy & Security > Tracking
• Android: Settings > Google > Ads`,
      },
      {
        title: "4.5 Global Privacy Control (GPC)",
        content: `We honor the Global Privacy Control (GPC) browser signal as an opt-out from sale or sharing of personal information under CCPA/CPRA and other applicable laws. If your browser sends a GPC signal, we will treat it as an opt-out of non-essential cookies and tracking to the extent technically feasible.`,
      },
    ],
  },
  {
    number: "5",
    title: "Do Not Track Signals",
    content: `Some browsers include a "Do Not Track" (DNT) feature. Currently there is no universally accepted standard for responding to DNT signals. We endeavor to honor user privacy preferences and review our practices as standards develop. We honor the Global Privacy Control (GPC) as an opt-out signal for users in jurisdictions where required by law.`,
  },
  {
    number: "6",
    title: "Jurisdiction-Specific Cookie Requirements",
    subsections: [
      {
        title: "6.1 European Union / EEA (ePrivacy Directive / GDPR)",
        content: `For EU/EEA users, we comply with the ePrivacy Directive and GDPR. We obtain prior, informed, and specific consent before placing non-essential cookies on your device. You may withdraw consent at any time through our cookie consent tool. Withdrawal of consent does not affect the lawfulness of processing based on consent before withdrawal.`,
      },
      {
        title: "6.2 United Kingdom",
        content: `For UK users, we comply with the Privacy and Electronic Communications Regulations (PECR) and UK GDPR. Consent is required for non-essential cookies, and we provide a lawful mechanism to manage your preferences.`,
      },
      {
        title: "6.3 California (CCPA/CPRA)",
        content: `California users may opt out of cookies and tracking that constitute a "sale" or "sharing" of personal information under CCPA/CPRA. Exercise this right through our cookie consent tool or by contacting social@furbabies.ai. We honor the GPC as an opt-out signal.`,
      },
      {
        title: "6.4 Canada",
        content: `For Canadian users, we obtain meaningful consent for non-essential cookies in accordance with PIPEDA and applicable provincial laws.`,
      },
      {
        title: "6.5 Brazil",
        content: `For Brazilian users, we comply with the LGPD regarding cookie consent and tracking. Non-essential cookies require prior consent.`,
      },
      {
        title: "6.6 Australia",
        content: `For Australian users, we comply with the Australian Privacy Act 1988 and the Australian Privacy Principles with respect to the collection of personal data through cookies.`,
      },
    ],
  },
  {
    number: "7",
    title: "Cookies and Children — 18+ Only",
    highlight_section: true,
    content: `THE SERVICE IS STRICTLY FOR USERS 18 YEARS OF AGE OR OLDER. WE DO NOT USE COOKIES TO COLLECT PERSONAL INFORMATION FROM INDIVIDUALS UNDER THE AGE OF 18.

Age Verification Cookie: We use the fb_age_verify cookie to record your confirmation at registration that you are 18 or older. This cookie is strictly necessary and cannot be disabled. It supports our COPPA compliance obligations.

No Tracking of Minors: We do not knowingly place analytics, functional, or targeting cookies on devices used by individuals under 18. If we discover that tracking data has been collected from a minor, we will:
• Promptly delete all associated cookie data;
• Terminate the associated account;
• Notify parents or guardians upon verified request.

Parental Rights: Parents and guardians who believe a minor has been subject to cookie-based tracking through this Service should contact social@furbabies.ai with subject "Minor Cookie Report." We will respond within five (5) business days.

COPPA Notice: This Service does not use cookies to collect personal information from children under 13 and is not directed at children under 13.`,
  },
  {
    number: "8",
    title: "Cookie Data Retention and Security",
    content: `Cookie data is retained for the duration indicated in the Cookie Inventory table in Section 3, or until you clear your cookies or withdraw consent, whichever occurs first.

We implement reasonable security measures to protect data collected through cookies. However, cookie data transmitted over the internet is subject to the inherent risks of internet transmission.

We regularly audit our cookie usage to ensure our Cookie Inventory is current and accurate. If you notice a cookie not listed in our inventory, please contact social@furbabies.ai.`,
  },
  {
    number: "9",
    title: "Changes to This Cookie Policy",
    content: `We may update this Cookie Policy to reflect changes in cookie usage or applicable law. We will notify you of material changes by posting the updated Policy on the Service and updating the effective date. We encourage you to review this Policy periodically.

Your continued use of the Service after the effective date of changes constitutes acceptance of the revised Policy.`,
  },
  {
    number: "10",
    title: "Contact Us",
    content: `For questions, concerns, or requests regarding this Cookie Policy:

Furbabies Inc.
495 Grand Blvd #206, Miramar Beach, FL 32550
Email: social@furbabies.ai

Minor Cookie Reports: social@furbabies.ai (Subject: "Minor Cookie Report")
Cookie Consent Issues: social@furbabies.ai (Subject: "Cookie Consent")

We will respond to all cookie-related inquiries within thirty (30) days.`,
  },
];

const SectionBlock = ({ section, index }) => {
  const [open, setOpen] = useState(true);
  const isChild = section.number === "7";

  const headerBg    = isChild ? "linear-gradient(135deg,#fff5f5,#fef2f2)" : "#f4ede7";
  const headerHover = isChild ? "linear-gradient(135deg,#fee2e2,#fef2f2)" : "#ede4db";
  const borderCol   = isChild ? "rgba(220,38,38,0.35)" : "#e8ddd6";
  const numBg       = isChild ? "#dc2626" : "#967d6c";
  const chevronCol  = isChild ? "#dc2626" : "#967d6c";

  return (
    <motion.div
      initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
      transition={{ duration:0.5, delay: index * 0.04 }}
      className="mb-4 rounded-2xl overflow-hidden"
      style={{ border:`1.5px solid ${borderCol}` }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left transition-colors"
        style={{ background: headerBg }}
        onMouseEnter={e => e.currentTarget.style.background = headerHover}
        onMouseLeave={e => e.currentTarget.style.background = headerBg}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <span className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center flex-shrink-0"
            style={{ background: numBg }}>{section.number}</span>
          <h2 className="text-lg font-bold text-gray-800">{section.title}</h2>
          {isChild && (
            <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:99, background:"rgba(220,38,38,0.10)", border:"1px solid rgba(220,38,38,0.30)", fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#dc2626", marginLeft:6, flexShrink:0 }}>
              ⚠ 18+ / COPPA
            </span>
          )}
        </div>
        <span className={`text-xl transition-transform duration-200 flex-shrink-0 ml-3 ${open ? "rotate-180" : ""}`}
          style={{ color: chevronCol }}>▾</span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }}
            transition={{ duration:0.25, ease:"easeInOut" }} style={{ overflow:"hidden" }}>
            <div className={`px-6 py-5 ${isChild ? "bg-[#fff5f5]" : "bg-white"}`}>
              {section.content && <p className="text-gray-600 poppins-text text-sm leading-7 whitespace-pre-line">{section.content}</p>}
              {section.subsections && section.subsections.map((sub, i) => (
                <div key={i} className="mb-5 last:mb-0">
                  <h3 className="text-sm font-bold text-gray-800 mb-2">{sub.title}</h3>
                  <p className="text-gray-600 poppins-text text-sm leading-7 whitespace-pre-line">{sub.content}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CookiePolicy = () => (
  <>
    <Helmet>
      <title>Cookie Policy — Furbabies.AI</title>
      <meta name="description" content="Read the Furbabies.AI Cookie Policy." />
    </Helmet>
    <Navbar />
    <div className="min-h-screen bg-white pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
          className="mb-12 text-center mt-20">
          <p className="text-[#967d6c] font-semibold poppins-text text-sm uppercase tracking-widest mb-3">Legal</p>
          <h1 className="text-5xl lg:text-[60px] lg:leading-[70px] font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="poppins-text text-gray-500 text-sm">Effective Date: March 10, 2026 &nbsp;·&nbsp; Last Updated: March 10, 2026</p>
          <div className="mt-4 inline-block bg-[#fdf8f5] border border-[#e8ddd6] rounded-full px-5 py-2">
            <p className="text-xs poppins-text text-gray-500">
              Furbabies Inc. · 495 Grand Blvd #206, Miramar Beach, FL 32550 ·{" "}
              <a href="mailto:social@furbabies.ai" className="text-[#967d6c] hover:underline">social@furbabies.ai</a>
            </p>
          </div>
        </motion.div>

        {/* Intro notice */}
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.3, duration:0.6 }}
          className="mb-8 bg-[#f4ede7] border border-[#e8ddd6] rounded-2xl px-6 py-5">
          <p className="text-sm text-gray-700 poppins-text leading-7">
            This Cookie Policy explains how Furbabies Inc. uses cookies and similar tracking technologies on the Furbabies.AI platform. By using the Service, you consent to our use of cookies as described herein. This Service is for users <strong>18 and older only</strong> — we do not use cookies to track minors.
          </p>
        </motion.div>

        {/* Sections 1-2 */}
        {sections.slice(0, 2).map((section, i) => <SectionBlock key={i} section={section} index={i} />)}

        {/* Cookie Inventory Table — Section 3 */}
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
          transition={{ duration:0.5, delay:0.1 }}
          className="mb-4 rounded-2xl overflow-hidden border border-[#e8ddd6]">
          <div className="flex items-center gap-4 px-6 py-4 bg-[#f4ede7]">
            <span className="w-8 h-8 rounded-full bg-[#967d6c] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
            <h2 className="text-lg font-bold text-gray-800">Cookie Inventory</h2>
          </div>
          <div className="bg-white px-4 py-4 overflow-x-auto">
            <p className="text-xs text-gray-500 poppins-text mb-4 px-2">
              The following table provides an overview of the cookies we use. This list is updated periodically as our Service evolves. Note the inclusion of <strong>fb_age_verify</strong>, a strictly necessary cookie supporting our 18+ age compliance obligations.
            </p>
            <table className="w-full text-xs poppins-text border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-[#f4ede7]">
                  {["Cookie Name","Category","Purpose","Duration","Third Party?"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 font-bold text-gray-700 border-b border-[#e8ddd6]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cookieInventory.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#fdf8f5]"}>
                    <td className="px-3 py-2.5 font-mono text-gray-800 border-b border-[#f0e8e1]">{row.name}</td>
                    <td className="px-3 py-2.5 border-b border-[#f0e8e1]">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${categoryColor[row.category] || "bg-gray-100 text-gray-600"}`}>
                        {row.category}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 border-b border-[#f0e8e1]">{row.purpose}</td>
                    <td className="px-3 py-2.5 text-gray-600 border-b border-[#f0e8e1] whitespace-nowrap">{row.duration}</td>
                    <td className="px-3 py-2.5 text-gray-600 border-b border-[#f0e8e1] whitespace-nowrap">{row.thirdParty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 poppins-text mt-3 px-2 italic">
              Note: The above inventory is representative and may not be exhaustive. We conduct periodic cookie audits to keep this inventory current. Contact social@furbabies.ai if you identify a cookie not listed above.
            </p>
          </div>
        </motion.div>

        {/* Remaining sections */}
        {sections.slice(2).map((section, i) => <SectionBlock key={i + 3} section={section} index={i + 3} />)}

        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label:"Terms & Conditions", to:"/terms",         desc:"Our terms of service and user agreement" },
            { label:"Privacy Policy",     to:"/privacy-policy", desc:"How we collect and use your data" },
          ].map(item => (
            <Link key={item.to} to={item.to}
              className="flex items-center justify-between bg-[#f4ede7] hover:bg-[#ede4db] transition-colors rounded-2xl px-6 py-4 group">
              <div>
                <p className="font-bold text-gray-800 text-sm">{item.label}</p>
                <p className="text-xs text-gray-500 poppins-text mt-0.5">{item.desc}</p>
              </div>
              <span className="text-[#967d6c] text-lg group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          ))}
        </motion.div>

      </div>
    </div>
    <Footer />
  </>
);

export default CookiePolicy;